import { beautifyDate } from 'modules/analytics/Transformers';
import { ViewChart } from 'modules/analytics/models';
import moment from 'moment';
import { AttendanceConstants } from '../AttendanceConstants';
import * as styles from './report.module.scss';
import { maybePeriodFromCookies, setupPeriodToCookies } from 'modules/analytics/Utils';
import { AttendanceFeatures } from 'modules/application/detail/attendance/AttendanceFeatures';
import { sortByStringLabel } from 'data/function';
import CSVParser from 'assets/js/export-csv';
import DownloadFileUrl from 'assets/js/downloadFileUrl';
import { getChannelOptions } from 'modules/application/detail/attendance/report/ReportUtils';
import { DataExtractorModalController } from './dataExtractorModal/DataExtractorModalController';
import DataExtractorModalView from './dataExtractorModal/DataExtractorModal.html';
import { getDaysToToday, getDateFromISOFormat, DATA_RETENTION_DAYS_LIMIT, formatTimeString, formatEmptyTimeV1, ZERO_HOUR_MINUTE_SECOND, } from 'modules/application/detail/attendance/AttendanceUtils';
import { DATA_RETENTION_ERROR_DISPLAY, REPORTS_FILTERED, REPORT_STATUS_EXPORTED, REPORT_PERSONALIZED_BREAK_EXPORTED, } from 'modules/application/detail/attendance/AttendanceSegmentEvents';
import { DeskControllerBase } from '../DeskControllerBase';
import { FilterTypes } from '../FilterTypes';
import { SortingOrder } from 'modules/application/misc/SortingParams';
import { ReasonCodes } from 'types/ReasonCodes';
import { ChartType } from 'modules/analytics/models/Chart';
import { ReportConstantsTracks } from './ReportConstants';
import { ReportFeatures } from './ReportFeatures';
const DATE_FORMAT = 'YYYY-MM-DD';
const EX_DATE_FORMAT = 'DD/MM/YYYY';
const US_DATE_FORMAT = 'MM/DD/YYYY';
const AGENTS_TO_GET = 999;
var StatusTableCategories;
(function (StatusTableCategories) {
    StatusTableCategories["Name"] = "name";
    StatusTableCategories["Online"] = "online";
    StatusTableCategories["Paused"] = "paused";
    StatusTableCategories["Invisible"] = "invisible";
    StatusTableCategories["Total"] = "total";
})(StatusTableCategories || (StatusTableCategories = {}));
export class ReportController extends DeskControllerBase {
    static $inject = ['application', '$scope', 'DeskConfigurationService', 'LoadingService', 'ReportService', 'SegmentService', 'TeamService', '$translate', '$cookies', 'FileSaver', 'AccountService2', 'ConfigurationsService', 'BlipToastService', '$state', 'ModalService'];
    constructor(application, $scope, DeskConfigurationService, LoadingService, ReportService, SegmentService, TeamService, $translate, $cookies, FileSaver, AccountService2, ConfigurationsService, BlipToastService, $state, ModalService) {
        'ngInject';
        super(application, LoadingService, DeskConfigurationService, $scope);
        this.application = application;
        this.$scope = $scope;
        this.DeskConfigurationService = DeskConfigurationService;
        this.LoadingService = LoadingService;
        this.ReportService = ReportService;
        this.SegmentService = SegmentService;
        this.TeamService = TeamService;
        this.$translate = $translate;
        this.$cookies = $cookies;
        this.FileSaver = FileSaver;
        this.AccountService2 = AccountService2;
        this.ConfigurationsService = ConfigurationsService;
        this.BlipToastService = BlipToastService;
        this.$state = $state;
        this.ModalService = ModalService;
        this.channels = [];
        this.status = [];
        this.agents = [];
        this.teams = [];
        this.tags = [];
        this.agentsOptionsFixed = [];
        this.teamsOptionsFixed = [];
        this.agentsOptions = [];
        this.teamsOptions = [];
        this.tagsOptions = [];
        this.filteredChannels = [];
        this.filteredAgents = [];
        this.filteredTeams = [];
        this.filteredTags = [];
        this.isTextRewriteEnabled = false;
        this.removeChannel = async (event) => {
            const channel = event.detail.id;
            this.filteredChannels = this.filteredChannels.filter(c => c !== channel);
            await this.loadData();
        };
        this.removeAgent = async (event) => {
            const agent = event.detail.id;
            this.filteredAgents = this.filteredAgents.filter(a => a !== agent);
            await this.loadData();
        };
        this.removeTeam = async (event) => {
            const team = event.detail.id;
            this.filteredTeams = this.filteredTeams.filter(t => t !== team);
            await this.loadData();
        };
        this.removeTag = async (event) => {
            const tag = event.detail.id;
            this.filteredTags = this.filteredTags.filter(t => t !== tag);
            await this.loadData();
        };
    }
    async $onInit() {
        this.styles = styles;
        this.statusTableCategoriesEnum = { ...StatusTableCategories };
        this.isLoading = false;
        this.isLoadingReportPersonalizedBreak = false;
        this.isBuildingChart = true;
        this.cookiesPeriodKey = `${this.application.shortName}_attendancePeriodSetting`;
        this.user = this.AccountService2.meSync();
        const today = new Date();
        const defaultPeriod = {
            start: moment(new Date(today.getFullYear(), today.getMonth(), today.getDate()))
                .subtract(7, 'days')
                .format(DATE_FORMAT),
            end: moment(new Date(today.getFullYear(), today.getMonth(), today.getDate())).format(DATE_FORMAT),
        };
        this.period = maybePeriodFromCookies({
            $cookies: this.$cookies,
            key: this.cookiesPeriodKey,
            initialInterval: 7,
            period: defaultPeriod,
        });
        this.exPeriod = {
            start: moment(this.period.start, DATE_FORMAT).format(EX_DATE_FORMAT),
            end: moment(this.period.end, DATE_FORMAT).format(EX_DATE_FORMAT),
        };
        this.channels = getChannelOptions();
        this.setReportEmptyState();
        await Promise.all([
            this.verifyToggleFeatures(),
            this.loadTeams(),
            this.loadAgents(),
        ]);
        this.showDataRetentionAlert = false;
        const datesDiffByDays = getDaysToToday(getDateFromISOFormat(this.period.start));
        if (datesDiffByDays > DATA_RETENTION_DAYS_LIMIT) {
            this.displayDataRetentionAlert();
            return;
        }
        await this.loadData({ shouldLoadStatusReport: true, updateSelectOptions: true });
        const text = await this.$translate('report.helper.doc.text');
        const link = await this.$translate('report.helper.doc.link');
        this.helperLink = { text, link };
        this.SegmentService.createBotTrack('helpdesk-reports-opened', this.application);
    }
    async verifyToggleFeatures() {
        const [isTextRewriteEnabled, isWidgetBlipChatHelpCenterEnabled, isReportMfeEnabled,] = await Promise.all([
            ReportFeatures.isTextRewriteEnabled(),
            AttendanceFeatures.isWidgetBlipChatHelpCenterEnabled(),
            AttendanceFeatures.isReportMfeEnabled(),
        ]);
        this.isTextRewriteEnabled = isTextRewriteEnabled;
        this.isWidgetBlipChatHelpCenterEnabled = isWidgetBlipChatHelpCenterEnabled;
        if (isReportMfeEnabled) {
            this.$state.go('auth.application.detail.attendance.desk.report');
            return;
        }
    }
    async loadTeams() {
        const getTeamsResponse = await this.TeamService.getTeams(this.application);
        const teams = getTeamsResponse?.items ?? [];
        const teamsOptions = teams.map(t => ({ label: t.name, value: t.name }));
        teamsOptions.push({
            label: await this.$translate('directTransfer'),
            value: AttendanceConstants.DIRECT_TRANSFER_TEAM,
        });
        teamsOptions.sort(sortByStringLabel);
        this.teamsOptions = teamsOptions;
        this.teamsOptionsFixed = teamsOptions;
    }
    async loadAgents() {
        const agents = await this.TeamService.getAllAttendants('', AGENTS_TO_GET);
        const agentsOptions = agents.map(a => ({ label: a.fullName, value: a.identity }));
        agentsOptions.sort(sortByStringLabel);
        this.agentsOptions = agentsOptions;
        this.agentsOptionsFixed = agentsOptions;
    }
    goToDataExtractor() {
        this.SegmentService.createApplicationTrack({ trackEvent: ReportConstantsTracks.HELPDESK_REPORTS_DATAEXTRACTOR_BUTTON, application: this.application });
        this.$state.go('auth.application.detail.attendance.report.data-extractor');
    }
    async handleFilterChannels(items) {
        this.createFilterTrack(FilterTypes.Channel, items.length);
        this.filteredChannels = items;
        await this.loadData();
    }
    async handleFilterAgents(items) {
        this.createFilterTrack(FilterTypes.Agent, items.length);
        this.filteredAgents = items;
        await this.loadData();
    }
    async handleFilterTeams(items) {
        this.createFilterTrack(FilterTypes.Team, items.length);
        this.filteredTeams = items;
        await this.loadData();
    }
    async handleFilterTags(items) {
        this.createFilterTrack(FilterTypes.Tag, items.length);
        this.filteredTags = items;
        await this.loadData();
    }
    setReportEmptyState() {
        this.agentsCache = {};
        this.ticketsOpen = 0;
        this.ticketsLost = 0;
        this.ticketsAbandoned = 0;
        this.ticketsAttended = 0;
        this.ticketsClosed = 0;
        this.averageWaitingTime = this.averageCollapsedWaitingTime = ZERO_HOUR_MINUTE_SECOND;
        this.averageResponseTime = this.averageCollapsedResponseTime = ZERO_HOUR_MINUTE_SECOND;
        this.averageAttendanceTime = this.averageCollapsedAttendanceTime = ZERO_HOUR_MINUTE_SECOND;
        this.averageQueueTime = this.averageCollapsedQueueTime = ZERO_HOUR_MINUTE_SECOND;
        this.averageFirstResponseTime = this.averageCollapsedFirstResponseTime = ZERO_HOUR_MINUTE_SECOND;
        this.maxQueueTime = this.maxCollapsedQueueTime = ZERO_HOUR_MINUTE_SECOND;
        this.maxFirstResponseTime = this.maxCollapsedFirstResponseTime = ZERO_HOUR_MINUTE_SECOND;
        this.ticketsChart = new ViewChart({
            chartType: ChartType.Line,
            data: [],
        });
        this.tickets = {
            data: [],
            labels: [],
        };
        this.agents = [];
        this.tags = [];
        this.teams = [];
        this.status = [];
    }
    displayDataRetentionAlert() {
        this.setReportEmptyState();
        this.createRetentionDataTrack();
        this.showDataExtractorModal();
    }
    get usEndDate() {
        return moment(this.period.exEnd, EX_DATE_FORMAT).format(US_DATE_FORMAT);
    }
    get usStartDate() {
        return moment(this.period.exStart, EX_DATE_FORMAT).format(US_DATE_FORMAT);
    }
    /**
     * Change period date and load data
     */
    async changeDate() {
        this.isBuildingChart = true;
        this.period = {
            start: moment(this.exPeriod.start, EX_DATE_FORMAT).format(DATE_FORMAT),
            end: moment(this.exPeriod.end, EX_DATE_FORMAT).format(DATE_FORMAT),
        };
        const datesDiffByDays = getDaysToToday(getDateFromISOFormat(this.period.start));
        if (datesDiffByDays > DATA_RETENTION_DAYS_LIMIT) {
            this.displayDataRetentionAlert();
            return;
        }
        this.showDataRetentionAlert = false;
        setupPeriodToCookies({
            $cookies: this.$cookies,
            period: this.period,
            key: this.cookiesPeriodKey,
        });
        await this.loadData({ shouldLoadStatusReport: true, updateSelectOptions: true });
    }
    createFilterTrack(filteredBy, filterCount) {
        const payload = {
            filteredBy,
            filterCount,
            hasMultiplefilters: filterCount > 1,
        };
        this.SegmentService.createBotTrack(REPORTS_FILTERED, this.application, payload);
    }
    createRetentionDataTrack() {
        this.SegmentService.createBotTrack(DATA_RETENTION_ERROR_DISPLAY, this.application);
    }
    /**
     * Format agents summary, which needs email as value, for same-name people distinction
     *
     * @param items - items to format
     */
    formatStatusSummaryItems(items) {
        return items.map((item) => ({
            name: item.name,
            identity: item.identity,
            identityName: item.identityName,
            ...this.getFormattedStatusTimeStrings(item),
        }));
    }
    /**
     * Get formatted status timings
     *
     * @param item - status item, containing online, paused and invisible status values
     *
     * @return object with formatted online, paused and invisible values
     */
    getFormattedStatusTimeStrings(item) {
        return {
            online: formatTimeString(item.online),
            collapsedOnline: formatTimeString(item.online, true),
            paused: formatTimeString(item.paused),
            collapsedPaused: formatTimeString(item.paused, true),
            invisible: formatTimeString(item.invisible),
            collapsedInvisible: formatTimeString(item.invisible, true),
            total: formatTimeString(item.total),
            collapsedTotal: formatTimeString(item.total, true),
        };
    }
    /**
     * Get formatted average timings
     *
     * @param item - summary item, containing average time properties
     *
     * @return object with formmated averageFirstReponseTime, averageWaitTime, averageAttendanceTime and averageResponseTime values
     */
    getFormattedAverageTimeStrings(item) {
        const undefinedToDash = true;
        return {
            averageFirstResponseTime: this.formatTime({ value: item.averageFirstResponseTime, undefinedToDash }),
            averageCollapsedFirstResponseTime: this.formatTime({ value: item.averageFirstResponseTime, undefinedToDash, collapsed: true }),
            averageWaitTime: this.formatTime({ value: item.averageWaitTime, undefinedToDash }),
            averageCollapsedWaitTime: this.formatTime({ value: item.averageWaitTime, undefinedToDash, collapsed: true }),
            averageAttendanceTime: this.formatTime({ value: item.averageAttendanceTime, undefinedToDash }),
            averageCollapsedAttendanceTime: this.formatTime({ value: item.averageAttendanceTime, undefinedToDash, collapsed: true }),
            averageResponseTime: this.formatTime({ value: item.averageResponseTime, undefinedToDash }),
            averageCollapsedResponseTime: this.formatTime({ value: item.averageResponseTime, undefinedToDash, collapsed: true }),
        };
    }
    /**
     * Load entire page data based on current query
     *
     * @param shouldLoadStatusReport - if function should include status into loading promises
     */
    async loadData({ shouldLoadStatusReport = false, updateSelectOptions = false } = {}) {
        this.isBuildingChart = true;
        this.isLoading = true;
        const reportRequest = this.buildReportRequest();
        const ticketsReportPromise = this.getTicketsReportPromise(reportRequest);
        const ticketsTimingsPromise = this.getTicketsTimingsPromise(reportRequest);
        const agentsPromise = this.getAgentsReportPromise(reportRequest, updateSelectOptions);
        const teamsPromise = this.getTeamsReportPromise(reportRequest, updateSelectOptions);
        const tagsPromise = this.getTagsReportPromise(reportRequest);
        const statusPromise = shouldLoadStatusReport ? this.getStatusReportPromise(reportRequest) : undefined;
        try {
            await Promise.all([
                ticketsReportPromise,
                ticketsTimingsPromise,
                agentsPromise,
                teamsPromise,
                tagsPromise,
                statusPromise,
            ]);
        }
        catch (e) {
            console.error(e);
        }
        finally {
            try {
                if (shouldLoadStatusReport) {
                    this.resetStatusTableSorting();
                }
            }
            catch (e) {
                console.error(e);
            }
            this.isBuildingChart = false;
            this.isLoading = false;
        }
    }
    /**
     * Build report request for ReportService's query
     */
    buildReportRequest() {
        const channels = this.channels.filter(c => this.filteredChannels.includes(c.label)).map(c => c.value);
        const agents = this.agentsOptions.filter(a => this.filteredAgents.includes(a.label.trim())).map(a => a.value.trim());
        const teams = this.teamsOptions.filter(t => this.filteredTeams.includes(t.label)).map(t => t.value.replace(/,/g, AttendanceConstants.COMMA_CHAR_ESCAPED));
        const tags = this.tagsOptions.filter(t => this.filteredTags.includes(t.label)).map(t => t.value);
        return {
            beginDate: this.period.start,
            endDate: this.period.end,
            channels: channels,
            agents: agents,
            teams: teams,
            tags: tags,
        };
    }
    /**
     * Create promise for getting tickets report
     *
     * @param reportRequest - reportRequest for ReportService's query
     *
     * @return tickets report promise
     */
    async getTicketsReportPromise(reportRequest) {
        let ticketsReportLocal = [];
        const getTicketsReportResponse = await this.ReportService.getTicketsReport(reportRequest);
        if (getTicketsReportResponse?.items) {
            ticketsReportLocal = getTicketsReportResponse.items.length > 1
                ? getTicketsReportResponse.items.slice().sort((a, b) => {
                    return new Date(a.date).getTime() - new Date(b.date).getTime();
                })
                : getTicketsReportResponse.items;
        }
        this.ticketsReport = ticketsReportLocal;
        this.setTicketSummary(ticketsReportLocal);
        await this.setTickets(ticketsReportLocal);
    }
    /**
     * Create promise for getting tickets timings
     *
     * @param reportRequest - reportRequest for ReportService's query
     *
     * @return tickets timings promise
     */
    getTicketsTimingsPromise(reportRequest) {
        return this.ReportService.getTicketsTimings(reportRequest).then((summary) => {
            if (summary) {
                const zeroToDash = true;
                this.averageWaitingTime = this.formatTime({ value: summary.avgWaitTime, zeroToDash });
                this.averageCollapsedWaitingTime = this.formatTime({ value: summary.avgWaitTime, zeroToDash, collapsed: true });
                this.averageResponseTime = this.formatTime({ value: summary.avgResponseTime, zeroToDash });
                this.averageCollapsedResponseTime = this.formatTime({ value: summary.avgResponseTime, zeroToDash, collapsed: true });
                this.averageAttendanceTime = this.formatTime({ value: summary.avgAttendanceTime, zeroToDash });
                this.averageCollapsedAttendanceTime = this.formatTime({ value: summary.avgAttendanceTime, zeroToDash, collapsed: true });
                this.averageQueueTime = this.formatTime({ value: summary.avgQueueTime, zeroToDash });
                this.averageCollapsedQueueTime = this.formatTime({ value: summary.avgQueueTime, zeroToDash, collapsed: true });
                this.averageFirstResponseTime = this.formatTime({ value: summary.avgFirstResponseTime, zeroToDash });
                this.averageCollapsedFirstResponseTime = this.formatTime({ value: summary.avgFirstResponseTime, zeroToDash, collapsed: true });
                this.maxQueueTime = this.formatTime({ value: summary.maxQueueTime, zeroToDash });
                this.maxCollapsedQueueTime = this.formatTime({ value: summary.maxQueueTime, zeroToDash, collapsed: true });
                this.maxFirstResponseTime = this.formatTime({ value: summary.maxFirstResponseTime, zeroToDash });
                this.maxCollapsedFirstResponseTime = this.formatTime({ value: summary.maxFirstResponseTime, zeroToDash, collapsed: true });
            }
        });
    }
    /**
     * Create promise for getting agents report
     *
     * @param reportRequest - reportRequest for ReportService's query
     *
     * @return agents report promise
     */
    async getAgentsReportPromise(reportRequest, updateSelectOptions) {
        const attendantsReportResponse = await this.ReportService.getAttendantsReport(reportRequest);
        const agents = attendantsReportResponse?.items ?? [];
        await this.setAttendantsInfo(agents);
        if (updateSelectOptions) {
            const missingAgents = agents.filter(a => !this.agentsOptionsFixed.some(opt => opt.value === a.identity));
            const missingAgentOptions = missingAgents.map(a => ({ label: a.fullName, value: a.identity }));
            const agentsOptions = this.agentsOptionsFixed.concat(missingAgentOptions);
            agentsOptions.sort(sortByStringLabel);
            this.agentsOptions = agentsOptions;
        }
        this.agents = agents.map((item) => ({
            ...item,
            ...this.getFormattedAverageTimeStrings(item),
        }));
    }
    /**
     * Create promise for getting teams report
     *
     * @param reportRequest - reportRequest for ReportService's query
     *
     * @return teams report promise
     */
    async getTeamsReportPromise(reportRequest, updateSelectOptions) {
        const getTeamsReportResponse = await this.ReportService.getTeamsReport(reportRequest);
        let teams = [];
        if (getTeamsReportResponse?.items) {
            teams = getTeamsReportResponse.items.map((item) => ({
                ...item,
                ...this.getFormattedAverageTimeStrings(item),
            }));
            const directTransferTeam = teams.find(t => t.name === AttendanceConstants.DIRECT_TRANSFER_TEAM);
            if (directTransferTeam) {
                directTransferTeam.name = await this.$translate('directTransfer');
            }
        }
        if (updateSelectOptions) {
            const missingTeams = teams.filter(t => !this.teamsOptionsFixed.some(opt => opt.label === t.name));
            const missingTeamsOptions = missingTeams.map(t => ({ label: t.name, value: t.name }));
            const teamsOptions = this.teamsOptionsFixed.concat(missingTeamsOptions);
            teamsOptions.sort(sortByStringLabel);
            this.teamsOptions = teamsOptions;
        }
        this.teams = teams;
    }
    /**
     * Create promise for getting tags report
     *
     * @param reportRequest - reportRequest for ReportService's query
     *
     * @return tags report promise
     */
    async getTagsReportPromise(reportRequest) {
        const getTagsReportResponse = await this.ReportService.getTagsReport(reportRequest);
        let tags = [];
        if (getTagsReportResponse?.items) {
            tags = getTagsReportResponse.items.map((item) => ({
                ...item,
                ...this.getFormattedAverageTimeStrings(item),
            }));
        }
        this.tags = tags;
        if (this.tagsOptions.length === 0) {
            this.tagsOptions = tags.map(t => ({ label: t.name, value: t.name }));
        }
    }
    /**
     * Create promise for getting status report
     *
     * @param reportRequest - reportRequest for ReportService's query
     *
     * @return status report promise
     */
    async getStatusReportPromise(reportRequest) {
        const statusReportResponse = await this.ReportService.getAttendantStatusReport(reportRequest);
        let localStatus = statusReportResponse?.items ?? [];
        await this.setAttendantsInfo(localStatus);
        localStatus = this.formatStatusSummaryItems(localStatus);
        this.status = localStatus;
    }
    /**
     * Generate Status Report CSV
     *
     * @return a generated CSV based on the status report array
     */
    getGeneratedStatusReportCSV() {
        let csvArray = [];
        const agentHeader = this.$translate.instant('report.status.agent');
        const timeOnlineHeader = this.$translate.instant('report.status.time.online');
        const timeAwayHeader = this.$translate.instant('report.status.time.paused');
        const timeInvisibleHeader = this.$translate.instant('report.status.time.invisible');
        const timeTotalHeader = this.$translate.instant('report.status.time.total');
        csvArray = this.status.map((r) => {
            return {
                [`${agentHeader}`]: this.getNameOrIdentityName(r),
                [`${timeOnlineHeader}`]: r.online,
                [`${timeAwayHeader}`]: r.paused,
                [`${timeInvisibleHeader}`]: r.invisible,
                [`${timeTotalHeader}`]: r.total,
            };
        });
        return csvArray;
    }
    /**
     * Start CSV download for status report
     */
    async downloadStatusReportCSV() {
        try {
            const filename = 'blip-status-report.csv';
            const { file } = CSVParser.CSVAsBlob({
                data: this.getGeneratedStatusReportCSV(),
                hasHeader: true,
            });
            this.FileSaver.saveAs(file, filename);
            this.registerSegmentDownloadStatusReport();
        }
        catch (err) {
            console.error(err);
        }
    }
    /**
     * Set agents' info inside an array based on cache or on their accounts
     *
     * @param agents - agents' array
     */
    setAttendantsInfo(agents) {
        if (agents === undefined || agents.length === 0) {
            return;
        }
        const promises = agents.map(async (a) => {
            if (this.agentsCache.hasOwnProperty(a.identity)) {
                this.updateAttendantInfo(a, this.agentsCache[a.identity]);
                return a;
            }
            try {
                const agentAccount = await this.TeamService.getAccount(a.identity);
                this.updateAttendantInfo(a, agentAccount);
                this.agentsCache[a.identity] = agentAccount;
            }
            catch (e) {
                a.email = decodeURIComponent(a.identity.split('@')[0]);
                a.identityName = a.name = a.email;
                a.fullName = a.email.split('@')[0];
            }
        });
        return Promise.all(promises);
    }
    /**
     * Updated an agent's object based on a passed updated info
     *
     * @param target - targeted agent object to update
     * @param attendantInfo - updated info
     */
    updateAttendantInfo(target, attendantInfo) {
        if (!target || !attendantInfo) {
            return;
        }
        target.email = attendantInfo.email || decodeURIComponent(attendantInfo.identity.split('@')[0]);
        target.fullName = attendantInfo.fullName || target.email.split('@')[0];
        target.name = target.fullName;
        target.identityName = target.email;
    }
    setTicketSummary(ticketsReport) {
        this.ticketsOpen = ticketsReport.reduce((acc, i) => acc + i.open, 0);
        this.ticketsLost = ticketsReport.reduce((acc, i) => acc + i.missed, 0);
        this.ticketsAbandoned = ticketsReport.reduce((acc, i) => acc + i.closedClient, 0);
        this.ticketsAttended = ticketsReport.reduce((acc, i) => acc + i.closedAttendant + i.transferred, 0);
        this.ticketsClosed = ticketsReport.reduce((acc, i) => acc + i.closed, 0);
    }
    async setTickets(ticketsReport) {
        const open = await this.$translate('report.dashboard.tickets.open');
        const closed = await this.$translate('report.dashboard.tickets.closed');
        this.ticketsChart = new ViewChart({
            ...this.ticketsChart,
            data: [
                ['Day', open, closed],
                ...ticketsReport.reduce((acc, i) => acc.concat([
                    [beautifyDate(i.date, 'MMM DD', false), i.open, i.closed],
                ]), []),
            ],
        });
    }
    /**
     * Reset status table sorting parameter to none
     */
    resetStatusTableSorting() {
        this.statusTableSortingParams = {
            category: undefined,
            order: SortingOrder.None,
        };
    }
    /**
     * Update status table sorting
     *
     * @param $event - event containing category and order for sorting
     */
    updateStatusTableSorting($event) {
        const { category, order } = $event;
        const isAscending = (order == SortingOrder.Ascending);
        let compareNames = false;
        this.status.sort((a, b) => {
            let aStringParameter;
            let bStringParameter;
            switch (category) {
                case this.statusTableCategoriesEnum.Name:
                    compareNames = true;
                    break;
                default:
                    compareNames = true;
                    break;
                case this.statusTableCategoriesEnum.Online:
                    aStringParameter = a.online || ZERO_HOUR_MINUTE_SECOND;
                    bStringParameter = b.online || ZERO_HOUR_MINUTE_SECOND;
                    break;
                case this.statusTableCategoriesEnum.Paused:
                    aStringParameter = a.paused || ZERO_HOUR_MINUTE_SECOND;
                    bStringParameter = b.paused || ZERO_HOUR_MINUTE_SECOND;
                    break;
                case this.statusTableCategoriesEnum.Invisible:
                    aStringParameter = a.invisible || ZERO_HOUR_MINUTE_SECOND;
                    bStringParameter = b.invisible || ZERO_HOUR_MINUTE_SECOND;
                    break;
                case this.statusTableCategoriesEnum.Total:
                    aStringParameter = a.total || ZERO_HOUR_MINUTE_SECOND;
                    bStringParameter = b.total || ZERO_HOUR_MINUTE_SECOND;
                    break;
            }
            if (!compareNames) {
                const comparison = this.compareTimings(aStringParameter, bStringParameter, isAscending);
                if (comparison !== 0) {
                    return comparison;
                }
            }
            // Use fullName as tie breaker
            if (this.getNameOrIdentityName(a).toLowerCase() < this.getNameOrIdentityName(b).toLowerCase()) {
                return (isAscending ? -1 : 1);
            }
            if (this.getNameOrIdentityName(a).toLowerCase() > this.getNameOrIdentityName(b).toLowerCase()) {
                return (isAscending ? 1 : -1);
            }
            return 0;
        });
    }
    /**
     * Compare two timings, A and B, and return sort comparison value
     *
     * @param a - timing A, in formatted "1d00:00:00" style
     * @param b - timing B, in formatted "1d00:00:00" style
     * @param isAscending - order of sorting
     *
     * @return integer as result of comparison: 1, -1 or 0
     */
    compareTimings(a, b, isAscending) {
        const aHasDays = a.includes('d');
        const bHasDays = b.includes('d');
        const aSplit = aHasDays
            ? a.split('d')
            : undefined;
        const bSplit = bHasDays
            ? b.split('d')
            : undefined;
        const aDays = aSplit
            ? parseInt(aSplit[0])
            : 0;
        const bDays = bSplit
            ? parseInt(bSplit[0])
            : 0;
        const aHours = aSplit
            ? aSplit[1]
            : a;
        const bHours = bSplit
            ? bSplit[1]
            : b;
        if (aHasDays && bHasDays) {
            if (aDays > bDays) {
                return (isAscending ? 1 : -1);
            }
            if (aDays < bDays) {
                return (isAscending ? -1 : 1);
            }
        }
        if (aDays > bDays || (aDays === bDays && aHours.toLowerCase() > bHours.toLowerCase())) {
            return (isAscending ? 1 : -1);
        }
        if (bDays > aDays || (aDays === bDays && aHours.toLowerCase() < bHours.toLowerCase())) {
            return (isAscending ? -1 : 1);
        }
        return 0;
    }
    /**
     * Get agents's name + identity for tracking
     *
     * @param item - array item
     */
    getAgentsTrackId(item) {
        return `${item.name}-${item.identity}`;
    }
    /**
     * Get tags's name + identity name for tracking
     *
     * @param item - array item
     */
    getTagsTrackId(item) {
        return `${item.name}-${item.identityName}`;
    }
    /**
     * Get item's name or identity name
     *
     * @param item - array item
     */
    getNameOrIdentityName(item) {
        return item.name || item.identityName || decodeURIComponent(item.identity.split('@')[0]) || '';
    }
    /**
     * Send Download Status Report tracking to Segment
     */
    registerSegmentDownloadStatusReport() {
        this.SegmentService.createApplicationTrack({ trackEvent: REPORT_STATUS_EXPORTED, payload: {}, application: this.application });
    }
    /**
    * Send Download Personalized Breaks Report tracking to Segment
    */
    registerSegmentDownloadPersonalizedBreakReport() {
        this.SegmentService.createApplicationTrack({
            trackEvent: REPORT_PERSONALIZED_BREAK_EXPORTED,
            payload: {},
            application: this.application,
        });
    }
    getDateFilterPersonalizedBreakHistory() {
        const { start, end } = this.period;
        const { offset } = this.ConfigurationsService.loadTimezones(this.user);
        const endDate = moment(end)
            .add(1, 'day')
            .format('YYYY-MM-DD');
        const filter = `startDate ge datetimeoffset'${start}T00:00:00${offset}' and startDate lt datetimeoffset'${endDate}T00:00:00${offset}'`;
        return encodeURIComponent(filter);
    }
    async getNamePersonalizedBreaksReport() {
        const formatDate = (this.user.culture !== 'en' ? EX_DATE_FORMAT : US_DATE_FORMAT);
        const startDate = moment(this.period.start, DATE_FORMAT).format(formatDate);
        const endDate = moment(this.period.end, DATE_FORMAT).format(formatDate);
        return await this.$translate('report.reportNamePersonalizedBreak', { startDate, endDate });
    }
    /**
    * Start CSV download for Breaks history report
    */
    async downloadPersonalizedBreaksReportCSV() {
        try {
            this.isLoadingReportPersonalizedBreak = true;
            const uriReport = await this.ReportService.getPersonalizedBreaksHistory(encodeURIComponent(this.user.email), this.getDateFilterPersonalizedBreakHistory());
            const reportName = await this.getNamePersonalizedBreaksReport();
            await DownloadFileUrl.dowloadFile(reportName, uriReport);
            this.registerSegmentDownloadPersonalizedBreakReport();
        }
        catch (error) {
            if (error.code !== ReasonCodes.ResourceNotFound) {
                const msg = await this.$translate('utils.errorMsg.3');
                this.BlipToastService.show('danger', { msg });
                console.error(error);
            }
            else {
                const msg = await this.$translate('report.reportType.resourceNotFound');
                this.BlipToastService.show('warning', {
                    msg,
                });
            }
        }
        finally {
            this.isLoadingReportPersonalizedBreak = false;
        }
    }
    async showDataExtractorModal() {
        await this.ModalService.showModal({
            template: DataExtractorModalView,
            controller: DataExtractorModalController,
            controllerAs: '$ctrl',
        });
    }
    formatTime({ value, zeroToDash = false, undefinedToDash = false, collapsed = false }) {
        value = formatTimeString(value, collapsed);
        return formatEmptyTimeV1(value, zeroToDash, undefinedToDash);
    }
    validateLabelKeyOnToggle(label) {
        const labelKey = this.isTextRewriteEnabled ? label.replace('report.dashboard', 'report.dashboardRewrite')
            .replace('report.list.common', 'report.list.commonRewrite')
            .replace('report.status', 'report.statusRewrite')
            : label;
        return labelKey;
    }
}
ReportController.$inject = ['application', '$scope', 'DeskConfigurationService', 'LoadingService', 'ReportService', 'SegmentService', 'TeamService', '$translate', '$cookies', 'FileSaver', 'AccountService2', 'ConfigurationsService', 'BlipToastService', '$state', 'ModalService'];
